/**
 * @file product-columns.tsx
 * @description Колонки реестра товаров для `ResponsiveList` (phase19.31, зона
 * «Товары», макет (а)).
 *
 * От 1024 — таблица, строка 56 (две линии): миниатюра 40 · Товар (название
 * одной строкой, второй линией «артикул · категория») · Цена · Остаток ·
 * На сайте · Обновлён. На 1024–1279 «Обновлён» сворачивается второй линией
 * в «На сайте» (`fold`), чтобы таблица не вылезала за блок.
 * До 1024 эти колонки не используются: телефон рисует `ProductsCompactList`
 * (строка с миниатюрой 48), состав тот же.
 *
 * Деньги — «от 350,00 ₽» / «350,00 ₽», цены нет — «—» (SC51). Стикеров с
 * эмодзи нет (SC29): состояние на сайте — одна пилюля по тону.
 */

import { AvailabilityCell } from "../AvailabilityCell";
import { ProductThumb } from "../ProductThumb";
import { StatusPill } from "../../ui/StatusPill";
import type { ListColumn } from "../../ui/responsive-list-types";
import { TwoLineCell } from "../../ui/TwoLineCell";
import type { CatalogCategory, CatalogProduct } from "../../../lib/api-catalog";
import { categoryLabel } from "../../../lib/catalog-category-tree";
import { productPriceText, productSkuCodes, shortDate } from "../../../lib/catalog-format";
import { PRODUCT_SITE_META, productSiteState } from "../../../lib/catalog-status";

/** Вторая линия названия: «артикул · категория». */
export function productSecondLine(product: CatalogProduct, categoriesById: Map<string, CatalogCategory>): string {
  const category = categoriesById.get(product.categoryId);
  return [productSkuCodes(product), category ? categoryLabel(category) : null].filter(Boolean).join(" · ");
}

export function buildProductColumns(categoriesById: Map<string, CatalogCategory>): ListColumn<CatalogProduct>[] {
  return [
    {
      key: "photo",
      header: "Фото",
      width: "w-[64px]",
      cell: (p) => <ProductThumb src={p.primaryImageUrl} alt="" className="size-10 shrink-0 rounded-md" />,
    },
    {
      key: "name",
      header: "Товар",
      cell: (p) => (
        <TwoLineCell
          primary={<span className="font-medium">{p.displayName}</span>}
          secondary={productSecondLine(p, categoriesById) || null}
        />
      ),
    },
    {
      key: "price",
      header: "Цена",
      width: "w-[132px]",
      align: "end",
      cell: (p) => <span className="whitespace-nowrap tabular-nums">{productPriceText(p)}</span>,
    },
    {
      key: "stock",
      header: "Остаток",
      width: "w-[104px]",
      align: "end",
      cell: (p) => <AvailabilityCell product={p} />,
    },
    {
      key: "site",
      header: "На сайте",
      width: "w-[140px]",
      cell: (p) => <StatusPill meta={PRODUCT_SITE_META[productSiteState(p)]} />,
    },
    {
      key: "updated",
      header: "Обновлён",
      width: "w-[112px]",
      cell: (p) => <span className="tabular-nums text-on-surface-variant">{shortDate(p.updatedAt)}</span>,
      fold: { into: "site", cell: (p) => `Обновлён ${shortDate(p.updatedAt)}` },
    },
  ];
}
